function first(source, paths, fallback = null) {
  for (const path of paths) {
    const value = path.split('.').reduce((current, part) => current?.[part], source);
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return fallback;
}

function number(source, paths) {
  const raw = first(source, paths);
  if (raw === null || raw === undefined || raw === '') return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

export function portfolioId(portfolio) {
  return String(first(portfolio, ['id', 'portfolio_id', 'uuid'], ''));
}

export function portfolioList(payload) {
  const items = Array.isArray(payload) ? payload : first(payload, ['items', 'portfolios', 'data'], []);
  return Array.isArray(items) ? items : [];
}

export function normalizeAsset(raw = {}) {
  const identity = raw.asset || raw.coin || raw.currency || {};
  const id = first(raw, ['asset_id', 'coin_id', 'id'], first(identity, ['id', 'asset_id'], ''));
  const slug = first(raw, ['slug', 'asset_slug'], first(identity, ['slug'], ''));
  const logo = first(raw, ['logo', 'logo_url', 'image'], first(identity, ['logo', 'logo_url', 'image'], ''));
  const status = String(first(raw, ['status'], first(identity, ['status'], ''))).toLowerCase();
  return {
    raw,
    id: String(id),
    slug: String(slug),
    name: String(first(raw, ['fa_name', 'faname', 'name'], first(identity, ['fa_name', 'faname', 'name'], id || 'دارایی'))),
    englishName: String(first(raw, ['name_en', 'english_name'], first(identity, ['name', 'name_en'], ''))),
    symbol: String(first(raw, ['symbol'], first(identity, ['symbol'], ''))).toUpperCase(),
    logo: String(logo),
    quantity: number(raw, ['amount', 'quantity', 'balance', 'total_amount', 'holding_amount']),
    valueUsd: number(raw, ['value_usd', 'current_value_usd', 'total_value_usd', 'market_value_usd', 'summary.value_usd']),
    valueIrt: number(raw, ['value_irt', 'value_toman', 'current_value_irt', 'current_value_toman', 'total_value_irt', 'summary.value_irt']),
    pnlUsd: number(raw, ['profit_loss_usd', 'pnl_usd', 'profit_usd', 'net_profit_usd', 'summary.pnl_usd']),
    pnlIrt: number(raw, ['profit_loss_irt', 'profit_loss_toman', 'pnl_irt', 'pnl_toman', 'profit_irt', 'summary.pnl_irt']),
    pnlPercentUsd: number(raw, ['pnl_usd_percentage', 'profit_loss_percent_usd', 'pnl_percent_usd', 'profit_percent_usd', 'summary.pnl_percent_usd']),
    pnlPercentIrt: number(raw, ['pnl_irt_percentage', 'profit_loss_percent_irt', 'profit_loss_percent_toman', 'pnl_percent_irt', 'pnl_percent_toman', 'summary.pnl_percent_irt']),
    totalSpentUsd: number(raw, ['total_spent_usd']),
    totalSpentIrt: number(raw, ['total_spent_irt', 'total_spent_toman']),
    totalRevenueUsd: number(raw, ['total_revenue_usd']),
    totalRevenueIrt: number(raw, ['total_revenue_irt', 'total_revenue_toman']),
    priceUsd: number(raw, ['price_usd', 'current_price_usd', 'usd', 'price.usd']),
    priceIrt: number(raw, ['price_irt', 'price_toman', 'current_price_irt', 'toman', 'price.irt']),
    updatedAt: first(raw, ['updated_at', 'modified_at', 'price_updated_at'], null),
    deprecated: raw.deprecated === true || identity.deprecated === true || raw.is_active === false || raw.is_deprecated === true || ['deprecated', 'inactive', 'delisted'].includes(status),
  };
}

export function normalizeDetail(payload = {}, fallback = {}) {
  const source = payload && typeof payload === 'object' ? payload : {};
  const rawAssets = first(source, ['assets', 'holdings', 'items', 'coins'], []);
  return {
    ...fallback,
    ...source,
    id: portfolioId(source) || portfolioId(fallback),
    name: String(first(source, ['name', 'title'], first(fallback, ['name'], 'پورتفولیوی من'))),
    color: String(first(source, ['color'], first(fallback, ['color'], ''))),
    emoji: String(first(source, ['emoji'], first(fallback, ['emoji'], ''))),
    valueUsd: number(source, ['value_usd', 'current_value_usd', 'total_value_usd', 'balance_usd', 'summary.value_usd']),
    valueIrt: number(source, ['value_irt', 'value_toman', 'current_value_irt', 'total_value_irt', 'balance_irt', 'summary.value_irt']),
    pnlUsd: number(source, ['profit_loss_usd', 'pnl_usd', 'profit_usd', 'summary.pnl_usd']),
    pnlIrt: number(source, ['profit_loss_irt', 'profit_loss_toman', 'pnl_irt', 'pnl_toman', 'summary.pnl_irt']),
    pnlPercentUsd: number(source, ['profit_loss_percent_usd', 'pnl_percent_usd', 'summary.pnl_percent_usd']),
    pnlPercentIrt: number(source, ['profit_loss_percent_irt', 'profit_loss_percent_toman', 'pnl_percent_irt', 'summary.pnl_percent_irt']),
    updatedAt: first(source, ['updated_at', 'modified_at', 'price_updated_at'], null),
    assets: Array.isArray(rawAssets) ? rawAssets.map(normalizeAsset) : [],
    incomplete: Object.keys(source).length === 0,
  };
}

export function normalizeTransaction(raw = {}) {
  return {
    raw,
    id: String(first(raw, ['id', 'transaction_id', 'uuid'], '')),
    assetId: String(first(raw, ['asset_id', 'coin_id', 'asset.id'], '')),
    type: String(first(raw, ['type', 'transaction_type'], 'buy')).toLowerCase() === 'sell' ? 'sell' : 'buy',
    amount: number(raw, ['amount', 'quantity']),
    unitPriceUsd: number(raw, ['unit_price_usd', 'price_usd']),
    usdIrtRate: number(raw, ['usd_irt_rate', 'irt_rate', 'toman_rate']),
    time: first(raw, ['time', 'transaction_time', 'created_at'], null),
  };
}

export function transactionList(payload) {
  const list = Array.isArray(payload) ? payload : first(payload, ['items', 'transactions', 'data'], []);
  if (!Array.isArray(list)) return [];
  return sortTransactions(list.map(normalizeTransaction));
}

export function sortTransactions(transactions) {
  return [...transactions].sort((a, b) => {
    const time = new Date(b.time || 0).getTime() - new Date(a.time || 0).getTime();
    return time || String(b.id).localeCompare(String(a.id));
  });
}

export function chartPoints(payload) {
  const rows = Array.isArray(payload) ? payload : first(payload, ['points', 'items', 'data'], []);
  const numeric = (value) => value === null || value === undefined || value === '' ? Number.NaN : Number(value);
  return Array.isArray(rows) ? rows.filter((row) => Array.isArray(row) && row.length >= 2).map((row) => ({
    time: numeric(row[0]), usd: numeric(row[1]), rate: numeric(row[2]), btc: numeric(row[3]),
  })).filter((point) => Number.isFinite(point.time) && Number.isFinite(point.usd)) : [];
}
